import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  EsignFieldDefinition,
  EsignFieldType,
  SignerRole,
  SignerSlot,
  APPLICANT_SIGNER_SLOT_ID,
  LEGACY_ADMIN_SLOT_ID,
  LEGACY_ORG_REP_SLOT_ID,
  slotIdForField,
  pixelToPercentCoords,
  collectSelectionGroups,
  isOptionField,
} from '../../../lib/esign/pdfFieldRenderer';
import { loadPdfDocument, type LoadedPdf } from '../../../lib/esign/pdfDocumentRenderer';
import {
  APPLICANT_FIELD_CATALOGUE,
  isLinkableFieldType,
} from '../../../lib/esign/applicantFieldCatalogue';
import {
  FileSignature,
  UserCheck,
  Calendar,
  Type,
  CheckSquare,
  CircleDot,
  ListChecks,
  Trash2,
  Plus,
  Settings2,
  X,
  AlignLeft,
  Layers,
  Move,
  MousePointer2,
  Loader,
  AlertCircle,
  Rows3,
  Mail,
  Phone,
  CalendarDays,
  Hash,
  DollarSign,
  SquareChevronDown,
  MapPin,
  Building2,
  Briefcase,
  ClipboardCheck,
  FileText,
  Paperclip,
  Eye,
  ChevronDown,
} from 'lucide-react';

interface PdfFieldEditorProps {
  pdfUrl?: string; // Data URL or HTTP URL of the PDF document
  fields: EsignFieldDefinition[];
  onChange: (fields: EsignFieldDefinition[]) => void;
  readOnly?: boolean;
  selectedSignerRole?: SignerRole;
  /**
   * Multi-signer plan slots for this document. When provided, the
   * field settings assign fields to a SLOT (the Applicant slot, a
   * named external slot, or a legacy countersigner slot) instead of
   * the legacy three-role list.
   */
  signerSlots?: SignerSlot[];
  /** Legacy hint only — the loaded document itself is the page-count authority. */
  numPages?: number;
}

const FIELD_ICONS: Record<EsignFieldType, React.ElementType> = {
  signature: FileSignature,
  initials: UserCheck,
  fullName: Type,
  dateSigned: Calendar,
  text: AlignLeft,
  checkbox: CheckSquare,
  radioOption: CircleDot,
  checkboxOption: ListChecks,
  multilineText: Rows3,
  email: Mail,
  phone: Phone,
  date: CalendarDays,
  number: Hash,
  currency: DollarSign,
  dropdown: SquareChevronDown,
  address: MapPin,
  companyName: Building2,
  jobTitle: Briefcase,
  confirmation: ClipboardCheck,
  readOnlyText: FileText,
  attachmentUpload: Paperclip,
};

const FIELD_LABELS: Record<EsignFieldType, string> = {
  signature: 'Signature',
  initials: 'Initials',
  fullName: 'Full Name',
  dateSigned: 'Date Signed',
  text: 'Text Field',
  checkbox: 'Checkbox',
  radioOption: 'Radio Option',
  checkboxOption: 'Checkbox Option',
  multilineText: 'Multiline Text',
  email: 'Email',
  phone: 'Phone',
  date: 'Date',
  number: 'Number',
  currency: 'Currency',
  dropdown: 'Dropdown',
  address: 'Address',
  companyName: 'Company Name',
  jobTitle: 'Job Title',
  confirmation: 'Confirmation',
  readOnlyText: 'Read-Only Text',
  attachmentUpload: 'Attachment Upload',
};

/** Text-bearing types that take a character limit in the settings panel. */
const MAX_LENGTH_TYPES: EsignFieldType[] = [
  'text',
  'multilineText',
  'email',
  'phone',
  'address',
  'companyName',
  'jobTitle',
];

const FIELD_TYPES = Object.keys(FIELD_LABELS) as EsignFieldType[];

type LoadState = 'empty' | 'loading' | 'ready' | 'error';

export default function PdfFieldEditor({
  pdfUrl,
  fields,
  onChange,
  readOnly = false,
  selectedSignerRole = 'applicant',
  signerSlots,
}: PdfFieldEditorProps) {
  // Tool state: null = Select tool (the default). A field type is "armed"
  // only after its button is clicked; clicking it again, pressing Esc, or
  // choosing Select disarms it — page clicks then never place fields.
  const [activeTool, setActiveTool] = useState<EsignFieldType | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  // Signer preview: fields render as the signer will see them (anchored
  // inputs, option rings, read-only text) and placement/drag is off.
  const [previewMode, setPreviewMode] = useState(false);

  // Document state — pages come from the parsed PDF, never a manual count.
  const [loadState, setLoadState] = useState<LoadState>(pdfUrl ? 'loading' : 'empty');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadedPdf, setLoadedPdf] = useState<LoadedPdf | null>(null);
  const [pageWidth, setPageWidth] = useState(0);
  const [visiblePage, setVisiblePage] = useState(1);

  // Drag and resize state
  const [dragState, setDragState] = useState<{
    fieldId: string;
    pageNumber: number;
    mode: 'move' | 'resize';
    startX: number;
    startY: number;
    initialXPercent: number;
    initialYPercent: number;
    initialWidthPercent: number;
    initialHeightPercent: number;
  } | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef(new Map<number, HTMLDivElement>());
  const canvasRefs = useRef(new Map<number, HTMLCanvasElement>());
  const renderTokenRef = useRef(0);

  // ── Load + parse the document ──────────────────────────────────────
  useEffect(() => {
    if (!pdfUrl) {
      setLoadedPdf(null);
      setLoadState('empty');
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setLoadState('loading');
    setLoadError(null);
    loadPdfDocument(pdfUrl)
      .then((doc) => {
        if (cancelled) {
          doc.destroy();
          return;
        }
        setLoadedPdf(doc);
        setVisiblePage(1);
        setLoadState('ready');
      })
      .catch((err: any) => {
        if (cancelled) return;
        setLoadedPdf(null);
        setLoadError(err?.message || 'This file could not be read as a PDF.');
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  // Release the parsed document when replaced / unmounted.
  useEffect(() => {
    return () => {
      loadedPdf?.destroy();
    };
  }, [loadedPdf]);

  // ── Measure the pane so pages render at the width that fits ────────
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth - 32; // p-4 padding
      setPageWidth(Math.max(280, Math.min(880, w)));
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
  }, [loadState]);

  // ── Paint every page to its canvas ─────────────────────────────────
  useEffect(() => {
    if (!loadedPdf || loadState !== 'ready' || pageWidth <= 0) return;
    const token = ++renderTokenRef.current;
    (async () => {
      for (const p of loadedPdf.pages) {
        if (renderTokenRef.current !== token) return;
        const canvas = canvasRefs.current.get(p.pageNumber);
        if (!canvas) continue;
        try {
          await loadedPdf.renderPage(p.pageNumber, canvas, pageWidth);
        } catch (err: any) {
          // Superseded renders are cancelled by pdf.js — not an error.
          if (!/cancel/i.test(err?.message || '')) {
            console.warn(`PDF page ${p.pageNumber} render failed:`, err);
          }
        }
      }
    })();
  }, [loadedPdf, loadState, pageWidth]);

  // ── Keyboard: Esc disarms; Delete/Backspace removes the selection ──
  const handleRemoveField = useCallback(
    (fieldId: string, e?: React.MouseEvent) => {
      e?.stopPropagation();
      onChange(fields.filter((f) => f.id !== fieldId));
      if (selectedFieldId === fieldId) setSelectedFieldId(null);
    },
    [fields, onChange, selectedFieldId]
  );

  useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveTool(null);
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFieldId) {
        const t = e.target as HTMLElement | null;
        const tag = t?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
        e.preventDefault();
        handleRemoveField(selectedFieldId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [readOnly, selectedFieldId, handleRemoveField]);

  // ── Drag / resize against the field's own page ─────────────────────
  useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (e: MouseEvent) => {
      const wrapper = pageRefs.current.get(dragState.pageNumber);
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const deltaXPercent = ((e.clientX - dragState.startX) / rect.width) * 100;
      const deltaYPercent = ((e.clientY - dragState.startY) / rect.height) * 100;

      if (dragState.mode === 'move') {
        const newX = Math.max(0, Math.min(100 - dragState.initialWidthPercent, dragState.initialXPercent + deltaXPercent));
        const newY = Math.max(0, Math.min(100 - dragState.initialHeightPercent, dragState.initialYPercent + deltaYPercent));
        onChange(fields.map((f) => (f.id === dragState.fieldId ? { ...f, xPercent: newX, yPercent: newY } : f)));
      } else {
        const newWidth = Math.max(3, Math.min(100 - dragState.initialXPercent, dragState.initialWidthPercent + deltaXPercent));
        const newHeight = Math.max(2, Math.min(100 - dragState.initialYPercent, dragState.initialHeightPercent + deltaYPercent));
        onChange(fields.map((f) => (f.id === dragState.fieldId ? { ...f, widthPercent: newWidth, heightPercent: newHeight } : f)));
      }
    };

    const handleMouseUp = () => setDragState(null);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, fields, onChange]);

  // ── Placement: click on a page drops the armed field on THAT page ──
  const handlePageClick = (pageNumber: number) => (e: React.MouseEvent<HTMLDivElement>) => {
    if (readOnly || previewMode || dragState) return;
    if ((e.target as HTMLElement).closest('.esign-field-item')) return;

    // Select mode: clicking empty page area just clears the selection.
    if (!activeTool) {
      setSelectedFieldId(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    let defaultWidthPx = 140;
    let defaultHeightPx = 42;
    if (
      activeTool === 'checkbox' ||
      activeTool === 'radioOption' ||
      activeTool === 'checkboxOption' ||
      activeTool === 'confirmation'
    ) {
      defaultWidthPx = 28;
      defaultHeightPx = 28;
    } else if (activeTool === 'initials') {
      defaultWidthPx = 70;
      defaultHeightPx = 36;
    } else if (activeTool === 'multilineText' || activeTool === 'address') {
      defaultWidthPx = 220;
      defaultHeightPx = 64;
    } else if (activeTool === 'dropdown') {
      defaultWidthPx = 180;
      defaultHeightPx = 36;
    } else if (activeTool === 'attachmentUpload') {
      defaultWidthPx = 180;
      defaultHeightPx = 40;
    } else if (activeTool === 'readOnlyText') {
      defaultWidthPx = 220;
      defaultHeightPx = 36;
    }

    const { xPercent, yPercent, widthPercent, heightPercent } = pixelToPercentCoords(
      clickX,
      clickY,
      defaultWidthPx,
      defaultHeightPx,
      rect.width,
      rect.height
    );

    // Selection options are placed one by one over the printed circles /
    // squares. A newly placed option joins the most recent group of the
    // same kind (admins place a question's options consecutively), or
    // starts a fresh group; the settings panel can move it afterwards.
    // Option fields are never individually "required" — the GROUP's
    // rules govern (enforced by the group section + the server).
    let groupProps: Partial<EsignFieldDefinition> = {};
    if (activeTool === 'radioOption' || activeTool === 'checkboxOption') {
      const kind = activeTool === 'radioOption' ? 'radio' : 'checkbox';
      const sameKind = collectSelectionGroups(fields).filter((g) => g.kind === kind);
      const last = sameKind[sameKind.length - 1];
      if (last) {
        groupProps = {
          groupId: last.groupId,
          groupLabel: last.groupLabel,
          optionLabel: `Option ${last.members.length + 1}`,
          optionValue: `option_${last.members.length + 1}`,
          groupMinSelections: last.minSelections,
          groupMaxSelections: last.maxSelections,
          groupRequired: last.required,
        };
      } else {
        groupProps = {
          groupId: `grp_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
          groupLabel: 'New question',
          optionLabel: 'Option 1',
          optionValue: 'option_1',
          groupMinSelections: 0,
          groupMaxSelections: null,
          groupRequired: false,
        };
      }
    }

    // Extended-type starter config (Slice 3): currency gets its default
    // symbol, dropdowns one starter option, uploads the portal's common
    // formats, read-only text its authored content.
    let typeProps: Partial<EsignFieldDefinition> = {};
    if (activeTool === 'currency') {
      typeProps = { currencySymbol: '$' };
    } else if (activeTool === 'dropdown') {
      typeProps = { options: [{ label: 'Option 1', value: 'option_1' }] };
    } else if (activeTool === 'attachmentUpload') {
      typeProps = { acceptedFormats: ['pdf', 'jpg', 'png'], maxSizeMb: 10 };
    } else if (activeTool === 'readOnlyText') {
      typeProps = { readOnlyValue: 'Read only text' };
    }

    // With a signer plan, new fields default to the slot matching the
    // armed role (Applicant slot normally) so assignment starts right.
    const roleSlotId =
      selectedSignerRole === 'admin'
        ? LEGACY_ADMIN_SLOT_ID
        : selectedSignerRole === 'org_rep'
        ? LEGACY_ORG_REP_SLOT_ID
        : APPLICANT_SIGNER_SLOT_ID;
    const defaultSlotId =
      signerSlots && signerSlots.some((s) => s.id === roleSlotId) ? roleSlotId : undefined;

    const newField: EsignFieldDefinition = {
      id: `fld_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      fieldType: activeTool,
      pageNumber,
      xPercent,
      yPercent,
      widthPercent,
      heightPercent,
      // Options are governed by their group, and read-only text is never
      // "required" of the signer; everything else starts required
      // (confirmation included — the server enforces it regardless).
      required:
        activeTool === 'radioOption' || activeTool === 'checkboxOption' || activeTool === 'readOnlyText'
          ? false
          : true,
      signerRole: selectedSignerRole,
      ...(defaultSlotId ? { signerSlotId: defaultSlotId } : {}),
      label: `${FIELD_LABELS[activeTool]} ${fields.length + 1}`,
      fieldMapping: activeTool === 'fullName' ? 'personalDetails.fullName' : '',
      ...groupProps,
      ...typeProps,
    };

    onChange([...fields, newField]);
    setSelectedFieldId(newField.id);
    // The tool STAYS armed so several fields of one type can be placed
    // in a row; Esc / re-click / Select disarms it.
  };

  const handleStartDrag = (f: EsignFieldDefinition, mode: 'move' | 'resize') => (e: React.MouseEvent) => {
    if (readOnly) return;
    e.stopPropagation();
    setSelectedFieldId(f.id);
    setDragState({
      fieldId: f.id,
      pageNumber: f.pageNumber,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      initialXPercent: f.xPercent,
      initialYPercent: f.yPercent,
      initialWidthPercent: f.widthPercent,
      initialHeightPercent: f.heightPercent,
    });
  };

  const updateField = (fieldId: string, updates: Partial<EsignFieldDefinition>) => {
    onChange(fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)));
  };

  // Group-level edits (question text, rules) are written to EVERY member
  // of the group — group props are denormalized onto each member field,
  // so the members must never drift apart.
  const syncGroupProps = (field: EsignFieldDefinition, updates: Partial<EsignFieldDefinition>) => {
    if (!field.groupId) {
      updateField(field.id, updates);
      return;
    }
    onChange(fields.map((f) => (f.groupId === field.groupId ? { ...f, ...updates } : f)));
  };

  // Move an option to a different group (adopting that group's rules) or
  // start it in a fresh group of its own.
  const moveFieldToGroup = (field: EsignFieldDefinition, choice: string) => {
    if (choice === '__new') {
      updateField(field.id, {
        groupId: `grp_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
        groupLabel: 'New question',
        groupMinSelections: 0,
        groupMaxSelections: null,
        groupRequired: false,
      });
      return;
    }
    const target = collectSelectionGroups(fields).find((g) => g.groupId === choice);
    if (!target) return;
    updateField(field.id, {
      groupId: target.groupId,
      groupLabel: target.groupLabel,
      groupMinSelections: target.minSelections,
      groupMaxSelections: target.maxSelections,
      groupRequired: target.required,
    });
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || !loadedPdf) return;
    const probe = el.scrollTop + 48;
    let current = 1;
    for (const p of loadedPdf.pages) {
      const w = pageRefs.current.get(p.pageNumber);
      if (w && w.offsetTop <= probe) current = p.pageNumber;
    }
    setVisiblePage(current);
  };

  const scrollToField = (f: EsignFieldDefinition) => {
    setSelectedFieldId(f.id);
    pageRefs.current.get(f.pageNumber)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const selectedField = fields.find((f) => f.id === selectedFieldId);

  const renderField = (field: EsignFieldDefinition) => {
    const Icon = FIELD_ICONS[field.fieldType];
    const isSelected = selectedFieldId === field.id;
    return (
      <div
        key={field.id}
        data-testid={`esign-field-${field.id}`}
        onMouseDown={(e) => {
          if (readOnly) return;
          e.stopPropagation();
          setSelectedFieldId(field.id);
        }}
        style={{
          left: `${field.xPercent}%`,
          top: `${field.yPercent}%`,
          width: `${field.widthPercent}%`,
          height: `${field.heightPercent}%`,
        }}
        className={`esign-field-item absolute rounded border-2 transition-shadow flex items-center justify-between px-2 py-1 shadow-sm select-none ${
          isSelected
            ? 'bg-brand/20 border-brand ring-2 ring-brand/40 z-30'
            : field.signerRole === 'admin'
            ? 'bg-amber-100/90 border-amber-500 text-amber-900 z-10'
            : 'bg-blue-100/90 border-blue-600 text-blue-900 z-10'
        }`}
      >
        {/* Move Drag Handle */}
        {!readOnly && (
          <div
            onMouseDown={handleStartDrag(field, 'move')}
            className="absolute top-0 left-0 right-0 h-4 cursor-grab active:cursor-grabbing bg-black/10 hover:bg-black/20 flex items-center justify-center rounded-t"
            title="Click and drag to move field"
          >
            <Move className="h-2.5 w-2.5 text-slate-700" />
          </div>
        )}

        <div className="flex items-center gap-1 min-w-0 pointer-events-none mt-2">
          <Icon className="h-3.5 w-3.5 shrink-0 text-brand" />
          <span className="text-[10px] font-bold truncate">
            {isOptionField(field)
              ? `${field.groupLabel || 'Group'}: ${field.optionLabel || field.label}`
              : field.label}{' '}
            {(field.required || (isOptionField(field) && field.groupRequired)) && (
              <span className="text-rose-600">*</span>
            )}
          </span>
        </div>

        {!readOnly && isSelected && (
          <button aria-label="Remove field" title="Remove field"
            type="button"
            onClick={(e) => handleRemoveField(field.id, e)}
            className="p-0.5 text-rose-600 hover:bg-rose-100 rounded shrink-0 cursor-pointer mt-2"
          >
            <X className="h-3 w-3" />
          </button>
        )}

        {/* Corner Resize Handle */}
        {!readOnly && (
          <div
            onMouseDown={handleStartDrag(field, 'resize')}
            className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-brand text-white cursor-se-resize flex items-center justify-center rounded-tl text-[11px] font-bold z-40"
            title="Drag to resize field"
          >
            ↘
          </div>
        )}
      </div>
    );
  };

  // Signer preview: the same percent-anchored geometry, rendered the way
  // the signer will experience it — anchored inputs, option rings over
  // the printed shapes, authored read-only text, attachment affordance.
  const renderPreviewField = (field: EsignFieldDefinition) => {
    const style: React.CSSProperties = {
      left: `${field.xPercent}%`,
      top: `${field.yPercent}%`,
      width: `${field.widthPercent}%`,
      height: `${field.heightPercent}%`,
    };
    const box = 'absolute flex items-center px-2 text-[11px] rounded border select-none overflow-hidden bg-white/90 border-slate-300 text-slate-600';
    const hint = field.placeholder || field.label;
    let content: React.ReactNode;
    let className = box;
    switch (field.fieldType) {
      case 'readOnlyText':
        className = 'absolute px-2 py-1 text-[11px] text-slate-800 whitespace-pre-wrap select-none overflow-hidden';
        content = field.readOnlyValue || field.defaultValue || '';
        break;
      case 'radioOption':
        className = 'absolute flex items-center justify-center select-none';
        content = <span className="block rounded-full border-2 border-slate-500" style={{ width: '62%', height: '62%' }} />;
        break;
      case 'checkboxOption':
        className = 'absolute flex items-center justify-center select-none';
        content = <span className="block border-2 border-slate-500 rounded-[2px]" style={{ width: '62%', height: '62%' }} />;
        break;
      case 'checkbox':
      case 'confirmation':
        content = (
          <>
            <span className="block w-3.5 h-3.5 shrink-0 border-2 border-slate-500 rounded-[2px] mr-2" />
            <span className="truncate">{field.label}</span>
          </>
        );
        break;
      case 'dropdown': {
        const chosen = (field.options || []).find((o) => o.value === field.defaultValue);
        className = `${box} justify-between`;
        content = (
          <>
            <span className="truncate">{chosen ? chosen.label : field.placeholder || 'Select…'}</span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          </>
        );
        break;
      }
      case 'attachmentUpload':
        className = `${box} border-dashed gap-1.5`;
        content = (
          <>
            <Paperclip className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span className="truncate">Attach file{(field.acceptedFormats || []).length > 0 ? ` (${(field.acceptedFormats || []).join(', ')})` : ''}</span>
          </>
        );
        break;
      case 'signature':
      case 'initials':
        className = `${box} border-dashed italic text-slate-400`;
        content = field.fieldType === 'signature' ? 'Sign here' : 'Initial here';
        break;
      case 'multilineText':
      case 'address':
        className = `${box} items-start py-1 whitespace-pre-wrap`;
        content = field.defaultValue || hint;
        break;
      case 'date':
        content = field.defaultValue || field.placeholder || 'Pick a date';
        break;
      case 'currency':
        content = field.defaultValue || `${field.currencySymbol || '$'}0.00`;
        break;
      case 'dateSigned':
        content = 'Date auto-filled at signing';
        break;
      default:
        content = field.defaultValue || hint;
        break;
    }
    return (
      <div key={field.id} data-testid={`esign-preview-field-${field.id}`} style={style} className={className}>
        {content}
      </div>
    );
  };

  return (
    <div
      data-testid="esign-field-editor"
      className="flex flex-col lg:flex-row gap-4 w-full min-h-[520px] h-[calc(100dvh-170px)] max-h-[1240px]"
    >
      {/* Sidebar Toolbar — stays beside the canvas: the editor owns a
          definite viewport-bounded height, so this panel never scrolls
          away with the document; its own overflow handles long content
          (on small screens it is height-capped above the canvas). */}
      {!readOnly && (
        <div className="w-full lg:w-72 shrink-0 min-h-0 max-h-[45%] lg:max-h-full bg-slate-50 border border-slate-200 rounded-2xl p-4 flex flex-col gap-4 overflow-y-auto">
          <div>
            <h4 className="text-caption font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5 text-brand" />
              <span>Add Signing Field</span>
            </h4>

            {/* Select tool — the default state; page clicks place nothing. */}
            <button
              type="button"
              data-testid="tool-select"
              onClick={() => setActiveTool(null)}
              className={`w-full p-2.5 rounded-xl border text-left transition-all cursor-pointer flex items-center gap-2 mb-2 ${
                activeTool === null
                  ? 'bg-brand text-white border-brand shadow-sm font-bold'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-brand/50 font-semibold'
              }`}
            >
              <MousePointer2 className={`h-4 w-4 ${activeTool === null ? 'text-white' : 'text-brand'}`} />
              <span className="text-[11px]">Select / Move</span>
            </button>

            <div className="grid grid-cols-2 gap-2">
              {FIELD_TYPES.map((type) => {
                const Icon = FIELD_ICONS[type];
                const isActive = activeTool === type;
                return (
                  <button
                    key={type}
                    type="button"
                    data-testid={`tool-${type}`}
                    onClick={() => setActiveTool(isActive ? null : type)}
                    className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col gap-1 items-start ${
                      isActive
                        ? 'bg-brand text-white border-brand shadow-sm font-bold'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-brand/50 font-semibold'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-brand'}`} />
                    <span className="text-[11px]">{FIELD_LABELS[type]}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              {activeTool
                ? `Click any page to place a ${FIELD_LABELS[activeTool]} field. The tool stays armed for the next one — press Esc or click the tool again to stop.`
                : 'Select tool is active: click a field to edit it, drag its header to move it. Choose a field type above to start placing.'}
            </p>
          </div>

          <hr className="border-slate-200" />

          {/* Selected Field Settings */}
          {selectedField ? (
            <div className="space-y-3 bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <div className="flex items-center gap-1.5">
                  <Settings2 className="h-4 w-4 text-brand" />
                  <span className="text-xs font-bold text-slate-800">Field Settings</span>
                </div>
                <button aria-label="Close" title="Close"
                  type="button"
                  onClick={() => setSelectedFieldId(null)}
                  className="text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">Field Label</label>
                <input
                  type="text"
                  value={selectedField.label}
                  onChange={(e) => updateField(selectedField.id, { label: e.target.value })}
                  className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                />
              </div>

              {selectedField.fieldType === 'readOnlyText' && (
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">Document Text</label>
                  <textarea
                    data-testid="readonly-text-input"
                    rows={3}
                    value={selectedField.readOnlyValue || ''}
                    onChange={(e) => updateField(selectedField.id, { readOnlyValue: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Printed on the document exactly as written. The signer sees it but cannot change it.
                  </p>
                </div>
              )}

              {!isOptionField(selectedField) &&
                selectedField.fieldType !== 'readOnlyText' &&
                selectedField.fieldType !== 'attachmentUpload' &&
                selectedField.fieldType !== 'confirmation' && (
                  <>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Placeholder</label>
                      <input
                        type="text"
                        data-testid="field-placeholder-input"
                        value={selectedField.placeholder || ''}
                        onChange={(e) => updateField(selectedField.id, { placeholder: e.target.value })}
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Default Value</label>
                      <input
                        type="text"
                        data-testid="field-default-input"
                        value={selectedField.defaultValue || ''}
                        onChange={(e) => updateField(selectedField.id, { defaultValue: e.target.value })}
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                      />
                    </div>
                  </>
                )}

              {isOptionField(selectedField) && (
                <div className="space-y-3 border border-brand/25 bg-brand/5 rounded-xl p-3" data-testid="group-settings">
                  <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">
                    Selection Group ({selectedField.fieldType === 'radioOption' ? 'choose one' : 'choose any'})
                  </p>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Group</label>
                    <select
                      data-testid="group-select"
                      value={selectedField.groupId || ''}
                      onChange={(e) => moveFieldToGroup(selectedField, e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-brand/20 focus:outline-none font-semibold"
                    >
                      {collectSelectionGroups(fields)
                        .filter((g) => g.kind === (selectedField.fieldType === 'radioOption' ? 'radio' : 'checkbox'))
                        .map((g) => (
                          <option key={g.groupId} value={g.groupId}>
                            {g.groupLabel} ({g.members.length} option{g.members.length === 1 ? '' : 's'})
                          </option>
                        ))}
                      <option value="__new">+ Start a new group…</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Question Text</label>
                    <input
                      type="text"
                      data-testid="group-label-input"
                      value={selectedField.groupLabel || ''}
                      onChange={(e) => syncGroupProps(selectedField, { groupLabel: e.target.value })}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Option Label</label>
                      <input
                        type="text"
                        data-testid="option-label-input"
                        value={selectedField.optionLabel || ''}
                        onChange={(e) => updateField(selectedField.id, { optionLabel: e.target.value })}
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Option Value</label>
                      <input
                        type="text"
                        data-testid="option-value-input"
                        value={selectedField.optionValue || ''}
                        onChange={(e) => updateField(selectedField.id, { optionValue: e.target.value })}
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                      />
                    </div>
                  </div>

                  {selectedField.fieldType === 'checkboxOption' && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Min Selections</label>
                        <input
                          type="number"
                          min={0}
                          data-testid="group-min-input"
                          value={selectedField.groupMinSelections ?? 0}
                          onChange={(e) =>
                            syncGroupProps(selectedField, {
                              groupMinSelections: Math.max(0, parseInt(e.target.value, 10) || 0),
                            })
                          }
                          className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Max Selections</label>
                        <input
                          type="number"
                          min={1}
                          placeholder="No limit"
                          data-testid="group-max-input"
                          value={selectedField.groupMaxSelections ?? ''}
                          onChange={(e) =>
                            syncGroupProps(selectedField, {
                              groupMaxSelections: e.target.value === '' ? null : Math.max(1, parseInt(e.target.value, 10) || 1),
                            })
                          }
                          className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="chk_group_required"
                      data-testid="group-required-toggle"
                      checked={!!selectedField.groupRequired}
                      onChange={(e) => syncGroupProps(selectedField, { groupRequired: e.target.checked })}
                      className="rounded border-slate-300 text-brand focus:ring-brand/20 cursor-pointer"
                    />
                    <label htmlFor="chk_group_required" className="text-xs font-semibold text-slate-700 cursor-pointer">
                      Signer must answer this group
                    </label>
                  </div>

                  <p className="text-[11px] text-slate-500">
                    {selectedField.fieldType === 'checkboxOption' && (selectedField.groupMaxSelections ?? null) === 1
                      ? 'Max is 1: this group behaves like a single choice — picking another option switches the selection.'
                      : 'Question text and rules apply to every option in this group and are kept in sync.'}
                  </p>
                </div>
              )}

              {MAX_LENGTH_TYPES.includes(selectedField.fieldType) && (
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">Character Limit</label>
                  <input
                    type="number"
                    min={1}
                    placeholder="No limit"
                    data-testid="field-maxlength-input"
                    value={selectedField.maxLength ?? ''}
                    onChange={(e) =>
                      updateField(selectedField.id, {
                        maxLength: e.target.value === '' ? undefined : Math.max(1, parseInt(e.target.value, 10) || 1),
                      })
                    }
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                  />
                </div>
              )}

              {(selectedField.fieldType === 'number' || selectedField.fieldType === 'currency') && (
                <div className="space-y-3 border border-slate-200 rounded-xl p-3" data-testid="range-settings">
                  <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">Value Range</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Minimum</label>
                      <input
                        type="number"
                        placeholder="None"
                        data-testid="field-min-input"
                        value={selectedField.minValue ?? ''}
                        onChange={(e) =>
                          updateField(selectedField.id, {
                            minValue: e.target.value === '' ? undefined : Number(e.target.value),
                          })
                        }
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Maximum</label>
                      <input
                        type="number"
                        placeholder="None"
                        data-testid="field-max-input"
                        value={selectedField.maxValue ?? ''}
                        onChange={(e) =>
                          updateField(selectedField.id, {
                            maxValue: e.target.value === '' ? undefined : Number(e.target.value),
                          })
                        }
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                      />
                    </div>
                  </div>
                  {selectedField.fieldType === 'currency' && (
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Currency Symbol</label>
                      <input
                        type="text"
                        maxLength={3}
                        data-testid="field-currency-symbol-input"
                        value={selectedField.currencySymbol ?? '$'}
                        onChange={(e) => updateField(selectedField.id, { currencySymbol: e.target.value })}
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                      />
                    </div>
                  )}
                  <p className="text-[11px] text-slate-500">
                    Enforced in the signing view and again on the server at submit.
                  </p>
                </div>
              )}

              {selectedField.fieldType === 'dropdown' && (
                <div className="space-y-2 border border-slate-200 rounded-xl p-3" data-testid="dropdown-options-editor">
                  <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">Dropdown Options</p>
                  {(selectedField.options || []).map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <input
                        type="text"
                        placeholder="Label"
                        data-testid={`dropdown-option-label-${idx}`}
                        value={opt.label}
                        onChange={(e) => {
                          const next = [...(selectedField.options || [])];
                          next[idx] = { ...next[idx], label: e.target.value };
                          updateField(selectedField.id, { options: next });
                        }}
                        className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                      />
                      <input
                        type="text"
                        placeholder="Value"
                        data-testid={`dropdown-option-value-${idx}`}
                        value={opt.value}
                        onChange={(e) => {
                          const next = [...(selectedField.options || [])];
                          next[idx] = { ...next[idx], value: e.target.value };
                          updateField(selectedField.id, { options: next });
                        }}
                        className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                      />
                      <button
                        type="button"
                        title="Move up"
                        disabled={idx === 0}
                        onClick={() => {
                          const next = [...(selectedField.options || [])];
                          [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                          updateField(selectedField.id, { options: next });
                        }}
                        className="px-1 text-slate-500 hover:text-slate-800 disabled:opacity-30 cursor-pointer"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        title="Move down"
                        disabled={idx === (selectedField.options || []).length - 1}
                        onClick={() => {
                          const next = [...(selectedField.options || [])];
                          [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                          updateField(selectedField.id, { options: next });
                        }}
                        className="px-1 text-slate-500 hover:text-slate-800 disabled:opacity-30 cursor-pointer"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        title="Remove option"
                        onClick={() =>
                          updateField(selectedField.id, {
                            options: (selectedField.options || []).filter((_, i) => i !== idx),
                          })
                        }
                        className="p-0.5 text-rose-600 hover:bg-rose-100 rounded cursor-pointer"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    data-testid="dropdown-add-option"
                    onClick={() => {
                      const n = (selectedField.options || []).length + 1;
                      updateField(selectedField.id, {
                        options: [...(selectedField.options || []), { label: `Option ${n}`, value: `option_${n}` }],
                      });
                    }}
                    className="w-full px-2 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-caption font-bold text-slate-600 cursor-pointer"
                  >
                    + Add option
                  </button>
                  <p className="text-[11px] text-slate-500">
                    The signer picks from this list at the field on the document; the chosen label is printed on the signed PDF.
                  </p>
                </div>
              )}

              {selectedField.fieldType === 'attachmentUpload' && (
                <div className="space-y-3 border border-slate-200 rounded-xl p-3" data-testid="attachment-settings">
                  <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">Attachment Rules</p>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Accepted Formats (comma separated)</label>
                    <input
                      type="text"
                      key={selectedField.id}
                      data-testid="attachment-formats-input"
                      defaultValue={(selectedField.acceptedFormats || []).join(', ')}
                      onChange={(e) =>
                        updateField(selectedField.id, {
                          acceptedFormats: e.target.value
                            .split(',')
                            .map((s) => s.trim().toLowerCase().replace(/^\./, ''))
                            .filter(Boolean),
                        })
                      }
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Max File Size (MB)</label>
                    <input
                      type="number"
                      min={1}
                      data-testid="attachment-maxsize-input"
                      value={selectedField.maxSizeMb ?? 10}
                      onChange={(e) =>
                        updateField(selectedField.id, {
                          maxSizeMb: Math.max(1, parseInt(e.target.value, 10) || 1),
                        })
                      }
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                    />
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Enforced on the server at upload. Files are stored with the signing record, not printed into the PDF.
                  </p>
                </div>
              )}

              {selectedField.fieldType === 'confirmation' && (
                <p className="text-[11px] text-slate-500" data-testid="confirmation-note">
                  Confirmation fields are always required: the signer must actively check the box to complete, and
                  the server enforces it even if the flag is missing.
                </p>
              )}

              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">Assigned Signer</label>
                {signerSlots && signerSlots.length > 0 ? (
                  <select
                    value={slotIdForField(selectedField)}
                    onChange={(e) => {
                      const slotId = e.target.value;
                      // Keep the legacy coarse role in sync for older
                      // readers: Applicant slot → applicant, the legacy
                      // Administrator slot → admin, any named external
                      // slot → org_rep.
                      updateField(selectedField.id, {
                        signerSlotId: slotId,
                        signerRole:
                          slotId === APPLICANT_SIGNER_SLOT_ID
                            ? 'applicant'
                            : slotId === LEGACY_ADMIN_SLOT_ID
                            ? 'admin'
                            : 'org_rep',
                      });
                    }}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-brand/20 focus:outline-none font-semibold"
                  >
                    {signerSlots.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                        {s.kind === 'applicant' ? ' (the applicant)' : ''}
                      </option>
                    ))}
                    {/* A field pinned to a slot no longer in the plan keeps its assignment visible. */}
                    {!signerSlots.some((s) => s.id === slotIdForField(selectedField)) && (
                      <option value={slotIdForField(selectedField)}>
                        {slotIdForField(selectedField) === LEGACY_ADMIN_SLOT_ID
                          ? 'Administrator'
                          : slotIdForField(selectedField) === LEGACY_ORG_REP_SLOT_ID
                          ? 'Organization Representative'
                          : 'Applicant'}
                      </option>
                    )}
                  </select>
                ) : (
                  <select
                    value={selectedField.signerRole}
                    onChange={(e) => updateField(selectedField.id, { signerRole: e.target.value as SignerRole })}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-brand/20 focus:outline-none font-semibold"
                  >
                    <option value="applicant">Applicant</option>
                    <option value="admin">Administrator</option>
                    <option value="org_rep">Organization Representative</option>
                  </select>
                )}
              </div>

              {isLinkableFieldType(selectedField.fieldType) && (
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">Applicant Auto-Fill Field</label>
                <select
                  value={selectedField.fieldMapping || ''}
                  onChange={(e) => updateField(selectedField.id, { fieldMapping: e.target.value })}
                  className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-brand/20 focus:outline-none"
                >
                  <option value="">— None (Manual Input) —</option>
                  {APPLICANT_FIELD_CATALOGUE.map((grp) => (
                    <optgroup key={grp.group} label={grp.group}>
                      {grp.options.map((opt) => (
                        <option key={opt.path} value={opt.path}>
                          {opt.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {selectedField.fieldMapping && (
                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      id="chk_signer_can_edit"
                      data-testid="signer-can-edit-toggle"
                      checked={selectedField.signerCanEdit !== false}
                      onChange={(e) => updateField(selectedField.id, { signerCanEdit: e.target.checked })}
                      className="rounded border-slate-300 text-brand focus:ring-brand/20 cursor-pointer"
                    />
                    <label htmlFor="chk_signer_can_edit" className="text-xs font-semibold text-slate-700 cursor-pointer">
                      Allow signer to edit this field
                    </label>
                  </div>
                )}
              </div>
              )}

              {!isOptionField(selectedField) &&
                selectedField.fieldType !== 'readOnlyText' &&
                selectedField.fieldType !== 'confirmation' && (
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="chk_required"
                  checked={selectedField.required}
                  onChange={(e) => updateField(selectedField.id, { required: e.target.checked })}
                  className="rounded border-slate-300 text-brand focus:ring-brand/20 cursor-pointer"
                />
                <label htmlFor="chk_required" className="text-xs font-semibold text-slate-700 cursor-pointer">
                  Mandatory field
                </label>
              </div>
              )}

              <button
                type="button"
                onClick={(e) => handleRemoveField(selectedField.id, e)}
                className="w-full mt-2 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 text-caption font-bold rounded-lg border border-rose-200 flex items-center justify-center gap-1.5 transition cursor-pointer"
              >
                <Trash2 className="h-3.5 w-3.5" />
                <span>Delete Field</span>
              </button>
            </div>
          ) : (
            <div className="p-4 bg-white rounded-xl border border-dashed border-slate-200 text-center">
              <Layers className="h-6 w-6 text-slate-300 mx-auto mb-1.5" />
              <p className="text-caption font-semibold text-slate-500">No field selected</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Click any field on the document to configure or drag it.</p>
            </div>
          )}

          {/* Placed Fields Summary */}
          <div className="mt-auto">
            <h5 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
              Placed Fields ({fields.length})
            </h5>
            <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
              {fields.map((f, i) => {
                const Icon = FIELD_ICONS[f.fieldType];
                return (
                  <div
                    key={f.id}
                    onClick={() => scrollToField(f)}
                    className={`flex items-center justify-between p-1.5 rounded-lg text-caption cursor-pointer transition ${
                      selectedFieldId === f.id ? 'bg-brand/10 border border-brand/30 text-brand font-bold' : 'hover:bg-slate-100 text-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Icon className="h-3.5 w-3.5 shrink-0 text-brand" />
                      <span className="truncate">{f.label || `Field ${i + 1}`}</span>
                      <span className="text-[11px] px-1 py-0.2 bg-slate-200 rounded text-slate-600">P.{f.pageNumber}</span>
                    </div>
                    <button aria-label="Remove field" title="Remove field"
                      type="button"
                      onClick={(e) => handleRemoveField(f.id, e)}
                      className="text-slate-400 hover:text-rose-600 p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Main Document View — every page, one scrollable pane. The pane
          is the ONLY scroller in the editor: min-h-0 inside the bounded
          root height makes the flex column actually constrain it. */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col bg-slate-100 border border-slate-200 rounded-2xl overflow-hidden relative">
        <div className="bg-white px-4 py-2.5 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-700">Document Canvas</span>
            {loadState === 'ready' && loadedPdf && (
              <span className="text-caption px-2 py-0.5 bg-slate-100 rounded-md font-mono text-slate-600">
                Page {visiblePage} of {loadedPdf.numPages}
              </span>
            )}
            {activeTool && !readOnly && (
              <span className="text-caption px-2 py-0.5 bg-brand/10 text-brand rounded-md font-bold">
                Placing: {FIELD_LABELS[activeTool]}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!readOnly && (
              <button
                type="button"
                data-testid="toggle-preview"
                onClick={() => setPreviewMode((v) => !v)}
                className={`px-2.5 py-1 rounded-lg border text-caption font-bold flex items-center gap-1.5 cursor-pointer transition ${
                  previewMode
                    ? 'bg-brand text-white border-brand'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-brand/50'
                }`}
              >
                <Eye className="h-3.5 w-3.5" />
                <span>{previewMode ? 'Back to editing' : 'Preview as signer'}</span>
              </button>
            )}
            <span className="text-caption text-slate-400 font-medium">Scroll to review every page</span>
          </div>
        </div>

        <div ref={scrollRef} onScroll={handleScroll} data-testid="esign-editor-scroll" className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 relative">
          {loadState === 'loading' && (
            <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-slate-400 gap-3">
              <Loader className="h-8 w-8 animate-spin text-brand" />
              <p className="text-xs font-bold text-slate-500">Loading document…</p>
              <p className="text-[11px] text-slate-500">Parsing pages for the editor</p>
            </div>
          )}

          {loadState === 'error' && (
            <div className="h-full min-h-[420px] flex items-center justify-center">
              <div className="max-w-sm w-full bg-white border border-rose-200 rounded-2xl p-6 text-center space-y-2">
                <div className="w-11 h-11 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center mx-auto">
                  <AlertCircle className="h-5 w-5" />
                </div>
                <p className="text-sm font-extrabold text-slate-900">Document could not be opened</p>
                <p className="text-caption text-slate-500">
                  {loadError || 'This file could not be read as a PDF.'} Field placement is disabled until a readable PDF is attached.
                </p>
              </div>
            </div>
          )}

          {loadState === 'empty' && (
            <div className="h-full min-h-[420px] flex items-center justify-center">
              <div className="max-w-sm w-full bg-white border border-dashed border-slate-300 rounded-2xl p-6 text-center space-y-2">
                <FileSignature className="h-10 w-10 text-slate-300 mx-auto" />
                <p className="text-sm font-extrabold text-slate-700">No document attached</p>
                <p className="text-caption text-slate-400">
                  Upload a PDF first — its real pages will appear here so fields land exactly where signers see them.
                </p>
              </div>
            </div>
          )}

          {loadState === 'ready' && loadedPdf && (
            <div className="mx-auto space-y-5" style={{ width: pageWidth || undefined }}>
              {loadedPdf.pages.map((p) => (
                <div
                  key={p.pageNumber}
                  data-testid={`esign-page-${p.pageNumber}`}
                  data-page={p.pageNumber}
                  ref={(el) => {
                    if (el) pageRefs.current.set(p.pageNumber, el);
                    else pageRefs.current.delete(p.pageNumber);
                  }}
                  onClick={handlePageClick(p.pageNumber)}
                  className={`relative bg-white shadow-lg rounded-sm border border-slate-300 select-none overflow-hidden ${
                    activeTool && !readOnly ? 'cursor-crosshair' : 'cursor-default'
                  }`}
                  style={{ width: pageWidth || undefined, height: pageWidth ? pageWidth * (p.heightPts / p.widthPts) : undefined }}
                >
                  <canvas
                    ref={(el) => {
                      if (el) canvasRefs.current.set(p.pageNumber, el);
                      else canvasRefs.current.delete(p.pageNumber);
                    }}
                    className="absolute inset-0 w-full h-full block"
                  />
                  {fields
                    .filter((f) => f.pageNumber === p.pageNumber)
                    .map((f) => (previewMode ? renderPreviewField(f) : renderField(f)))}
                  <span className="absolute bottom-1.5 right-2 text-[11px] font-mono font-bold text-slate-400 bg-white/80 px-1 rounded pointer-events-none">
                    {p.pageNumber}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
